// src/components/holaveciLocal/PagoLocalWompi.js
//
// D-23 — Pago del plan HolaVeci Local con el Widget de Wompi.
//
//   1. Cotiza el primer cobro (prorrateado por los días que faltan del mes).
//   2. "Pagar": el backend crea la transacción y devuelve llave pública,
//      referencia, monto y firma de integridad (el monto no se puede alterar).
//   3. Se abre el widget. Al cerrarse, el backend reconsulta la transacción
//      en Wompi y activa el plan si quedó aprobada.
//   4. Si queda pendiente (PSE, Nequi), se consulta cada 4 s hasta resolver.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Stack, Typography, Button, Alert, CircularProgress, Paper, Divider,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { pagoLocal, mensualidad as apiMensualidad } from '../../api/holaveciLocalApi';

const WIDGET_URL = 'https://checkout.wompi.co/widget.js';
const INTERVALO_MS = 4000;
const MAX_INTENTOS = 45; // ~3 minutos

const cop = (n) => (n ?? 0).toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });
const fecha = (iso) => iso
  ? new Date(iso).toLocaleDateString('es-CO', { day: 'numeric', month: 'long', timeZone: 'UTC' })
  : '';

function cargarWidget() {
  return new Promise((resolve, reject) => {
    if (window.WidgetCheckout) { resolve(); return; }
    const existente = document.querySelector(`script[src="${WIDGET_URL}"]`);
    if (existente) {
      existente.addEventListener('load', () => resolve());
      existente.addEventListener('error', () => reject(new Error('widget')));
      return;
    }
    const s = document.createElement('script');
    s.src = WIDGET_URL;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('widget'));
    document.body.appendChild(s);
  });
}

const MESES = ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','septiembre','octubre','noviembre','diciembre'];
const fechaHora = (iso) => {
  if (!iso) return '';
  const d = new Date(/Z$|[+-]\d\d:\d\d$/.test(iso) ? iso : `${iso}Z`);
  return d.toLocaleString('es-CO', { timeZone: 'America/Bogota', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });
};

/**
 * modo="plan"        primer pago del plan HolaVeci Local (D-23).
 * modo="mensualidad" pago de la cuenta de cobro pendiente del mes (D-24).
 */
export default function PagoLocalWompi({ onPagado, modo = 'plan' }) {
  const esMensualidad = modo === 'mensualidad';
  const [cotizacion, setCotizacion] = useState(null);
  const [cargando, setCargando]     = useState(true);
  const [procesando, setProcesando] = useState(false);
  const [pendiente, setPendiente]   = useState(null);   // referencia en espera
  const [error, setError]           = useState('');
  const poller = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        if (esMensualidad) {
          const m = await apiMensualidad.estado();
          if (!m.factura) { setError('No tienes mensualidades pendientes por pagar.'); return; }
          setCotizacion({ factura: m.factura, montoPrimerCobro: m.factura.total });
        } else {
          setCotizacion(await pagoLocal.cotizar());
        }
      } catch (e) {
        setError(e.mensajeUsuario || 'No se pudo calcular el valor a pagar.');
      } finally { setCargando(false); }
    })();
    return () => { if (poller.current) clearInterval(poller.current); };
  }, [esMensualidad]);

  const resolver = useCallback((r) => {
    const estado = r?.estado;
    if (estado === 'aprobada') {
      if (poller.current) clearInterval(poller.current);
      setPendiente(null);
      onPagado?.();
      return true;
    }
    if (estado === 'declinada' || estado === 'error' || estado === 'cancelada_por_usuario') {
      if (poller.current) clearInterval(poller.current);
      setPendiente(null);
      setError(r.mensajeError || 'El pago no fue aprobado. Puedes intentarlo de nuevo.');
      return true;
    }
    return false;
  }, [onPagado]);

  const esperar = useCallback((referencia) => {
    setPendiente(referencia);
    let intentos = 0;
    if (poller.current) clearInterval(poller.current);
    poller.current = setInterval(async () => {
      intentos += 1;
      try {
        const r = await pagoLocal.verificar(referencia);
        if (resolver(r)) return;
      } catch { /* reintenta en el siguiente ciclo */ }
      if (intentos >= MAX_INTENTOS) {
        clearInterval(poller.current);
        setPendiente(null);
        setError('Tu pago sigue en proceso. Cuando Wompi lo confirme se habilitará la activación; vuelve a entrar en unos minutos.');
      }
    }, INTERVALO_MS);
  }, [resolver]);

  const pagar = async () => {
    setError('');
    setProcesando(true);
    try {
      await cargarWidget();
      const c = esMensualidad ? await apiMensualidad.iniciar() : await pagoLocal.iniciar();
      const telefono = (c.telefono || '').replace(/^\+57/, '').replace(/\D/g, '');

      const checkout = new window.WidgetCheckout({
        currency: c.moneda,
        amountInCents: c.montoCentavos,
        reference: c.referencia,
        publicKey: c.publicKey,
        signature: { integrity: c.firmaIntegridad },
        customerData: {
          ...(c.correo ? { email: c.correo } : {}),
          ...(telefono.length === 10 ? { phoneNumber: telefono, phoneNumberPrefix: '+57' } : {}),
        },
      });

      // El widget solo llama al callback cuando hay una transacción; si el
      // usuario cierra la ventana sin pagar no hay aviso, por eso el botón
      // se libera apenas se abre.
      checkout.open(async (resultado) => {
        const tx = resultado?.transaction;
        if (!tx?.id) return;
        try {
          const r = await pagoLocal.confirmar(c.referencia, tx.id);
          if (!resolver(r)) esperar(c.referencia);
        } catch {
          // El webhook de Wompi también confirma: se sigue consultando.
          esperar(c.referencia);
        }
      });
      setProcesando(false);
    } catch (e) {
      setProcesando(false);
      setError(e.message === 'widget'
        ? 'No se pudo cargar la pasarela de pagos. Revisa tu conexión e inténtalo de nuevo.'
        : (e.mensajeUsuario || 'No se pudo iniciar el pago.'));
    }
  };

  if (cargando) {
    return <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={28} /></Box>;
  }

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

      {cotizacion?.factura && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="overline" color="text.secondary">
            Mensualidad {MESES[(cotizacion.factura.periodoMes || 1) - 1]} {cotizacion.factura.periodoAnio}
          </Typography>
          <Stack direction="row" justifyContent="space-between" alignItems="baseline">
            <Typography>Total a pagar</Typography>
            <Typography variant="h4" color="primary.main">{cop(cotizacion.factura.total)}</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Cuenta de cobro {cotizacion.factura.numeroFactura}
            {cotizacion.factura.detalle ? ` · ${cotizacion.factura.detalle}` : ''}
          </Typography>
          {cotizacion.factura.fechaLimitePago && (
            <Typography variant="body2" color={cotizacion.factura.vencida ? 'error.main' : 'text.secondary'} sx={{ mt: 0.5 }}>
              {cotizacion.factura.vencida
                ? 'Venció. HolaVeci se reactiva apenas se confirme el pago.'
                : `Paga antes del ${fechaHora(cotizacion.factura.fechaLimitePago)}.`}
            </Typography>
          )}
        </Paper>
      )}

      {cotizacion && !cotizacion.factura && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="overline" color="text.secondary">{cotizacion.planNombre}</Typography>
          <Stack direction="row" justifyContent="space-between" alignItems="baseline">
            <Typography>Pagas hoy</Typography>
            <Typography variant="h4" color="primary.main">{cop(cotizacion.montoPrimerCobro)}</Typography>
          </Stack>
          {cotizacion.esProrrateado && (
            <Typography variant="body2" color="text.secondary">
              Cubre {cotizacion.diasCubiertos} de {cotizacion.diasDelMes} días (hasta el {fecha(cotizacion.cubreHasta)}).
            </Typography>
          )}
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="body2" color="text.secondary">
            Desde el {fecha(cotizacion.proximoCobro)}: {cop(cotizacion.precioMensualCompleto)} al mes.
          </Typography>
        </Paper>
      )}

      {pendiente ? (
        <Alert severity="info" icon={<CircularProgress size={18} />}>
          Estamos confirmando tu pago con Wompi. No cierres esta página.
        </Alert>
      ) : (
        <Button
          variant="contained" size="large" color="secondary"
          startIcon={procesando ? <CircularProgress size={18} color="inherit" /> : <LockIcon />}
          disabled={procesando || !cotizacion}
          onClick={pagar}
        >
          {procesando ? 'Abriendo pago…' : 'Pagar con Wompi'}
        </Button>
      )}
      <Typography variant="caption" color="text.secondary" align="center">
        Pago seguro con tarjeta, PSE, Nequi o Bancolombia a través de Wompi.
      </Typography>
    </Stack>
  );
}
